import type { WorkspaceNodeMeta, WorkspaceNodePosition } from "../types/workspace";
import { isFederatedWorkspaceNodeId } from "../types/workspace";

const OPTIMISTIC_REPORT_NODE_PREFIX = "optimistic-report-";
const OPTIMISTIC_SIMULATION_NODE_PREFIX = "optimistic-simulation-";
const PENDING_ENTITY_PREFIX = "pending-";
const PLACEHOLDER_NODE_PREFIX = "placeholder:";

/** Same shape Postgres/`uuid` columns accept for workspace attach / report_id. */
const WORKSPACE_RESOURCE_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isOptimisticWorkspaceNodeId(nodeId: string): boolean {
  return (
    nodeId.startsWith(OPTIMISTIC_REPORT_NODE_PREFIX) ||
    nodeId.startsWith(OPTIMISTIC_SIMULATION_NODE_PREFIX)
  );
}

/**
 * True for real server UUIDs only. Rejects optimistic / pending / placeholder /
 * federated canvas ids that must never be sent as parentNodeId, workspaceId, or report_id.
 */
export function isPersistedWorkspaceResourceId(id: string | null | undefined): boolean {
  const trimmed = String(id ?? "").trim();
  if (!trimmed) return false;
  if (
    isOptimisticWorkspaceNodeId(trimmed) ||
    trimmed.startsWith(PENDING_ENTITY_PREFIX) ||
    trimmed.startsWith(PLACEHOLDER_NODE_PREFIX) ||
    isFederatedWorkspaceNodeId(trimmed)
  ) {
    return false;
  }
  return WORKSPACE_RESOURCE_UUID_RE.test(trimmed);
}

/** Omit attach fields when workspaceId or parentNodeId is not a persisted UUID. */
export function sanitizeWorkspaceSimulateAttachFields(fields: {
  workspaceId?: string | null;
  parentNodeId?: string | null;
  position?: WorkspaceNodePosition;
  meta?: WorkspaceNodeMeta;
}): {
  workspaceId?: string;
  parentNodeId?: string;
  position?: WorkspaceNodePosition;
  meta?: WorkspaceNodeMeta;
} {
  const workspaceId = String(fields.workspaceId ?? "").trim();
  const parentNodeId = String(fields.parentNodeId ?? "").trim();
  if (
    !isPersistedWorkspaceResourceId(workspaceId) ||
    !isPersistedWorkspaceResourceId(parentNodeId)
  ) {
    return {};
  }
  return {
    workspaceId,
    parentNodeId,
    ...(fields.position ? { position: fields.position } : {}),
    ...(fields.meta ? { meta: fields.meta } : {}),
  };
}

/** Real report UUID for simulate start; omit optimistic/pending placeholders. */
export function sanitizeSimulateReportId(
  reportId: string | null | undefined
): string | undefined {
  const id = String(reportId ?? "").trim();
  return isPersistedWorkspaceResourceId(id) ? id : undefined;
}
